import { useSnackbar } from '@app/src/context/SnackbarContext'
import type RequestError from '@app/src/errors/RequestError'
import { useIntl } from 'react-intl'
import { NotificationSeverity } from '@app/src/wf-constants'
import { Box } from '@mui/material'
import React from 'react'

export type ErrorNotificationProps = {
  requestError: RequestError | null
  specificFallbackErrorMessage?: string
  disableAutoClose?: boolean
}

const useErrorNotification = (): {
  showErrorNotification: (props: ErrorNotificationProps) => void
} => {
  const { formatMessage } = useIntl()
  const { showSnackbar } = useSnackbar()
  const defaultErrorMessage = formatMessage({ id: 'notifications.error' })

  const getErrorMessage = (requestError: RequestError | null, specificFallbackErrorMessage?: string) => {
    const fallbackErrorMessage = specificFallbackErrorMessage ?? defaultErrorMessage

    const errorCode = requestError?.body?.errorCode
    if (!errorCode) return fallbackErrorMessage

    const stringId = `errorMessages.${errorCode}`
    const errorMessage = formatMessage({ id: stringId })
    const errorMessageNotFound = errorMessage === stringId
    if (errorMessageNotFound) return fallbackErrorMessage
    return errorMessage
  }

  const showErrorNotification = ({
    requestError,
    disableAutoClose,
    specificFallbackErrorMessage,
  }: ErrorNotificationProps): void => {
    const errorMessage = getErrorMessage(requestError, specificFallbackErrorMessage)
    const sentryId = requestError?.body?.sentryId
    const statusCode = requestError?.statusCode

    showSnackbar({
      message: (
        <>
          <Box>{errorMessage}</Box>
          {(statusCode || sentryId) && (
            <Box
              mt={1.5}
              sx={({ typography }) => ({ opacity: 0.8, fontSize: typography.body2.fontSize, lineHeight: '1.6em' })}
            >
              {statusCode && <Box>HTTP {statusCode}</Box>}
              {sentryId && <Box>Error code: {sentryId}</Box>}
            </Box>
          )}
        </>
      ),
      severity: NotificationSeverity.error,
      disableAutoClose,
    })
  }

  return { showErrorNotification }
}

export default useErrorNotification
